/**
 * Responsibility: Implement forge contract parsing and normalization for AI/runtime boundaries.
 * Scope: Schema-backed boundary validation only; no orchestration or side effects.
 */
import { z } from 'zod'
import type {
  Ability,
  ActionCategory,
  ActionMode,
  AttributeKey,
  CompanionFeeling,
  CompanionMotive,
  ForgedItemPayload,
  GameEventState,
  GenesisAIResponse,
  LogicArtifactReplacements,
  LogicUpdate,
  NpcLogicUpdate,
  ProgressClock,
  QuestStatus,
  QuestType,
  RawGenesisCompanion,
  ScheduledEffectJobRequest,
  Suggestion,
  Trait,
  Skill,
  WorldTickUpdate,
} from "@anyquest/shared-core"
import { parseGameEventState } from './game-event-artifact'

const ATTR_KEYS = ['STR', 'DEX', 'CON', 'INT', 'WIS', 'CHA', 'PER', 'LCK', 'WIL', 'FOR', 'AGI', 'PRS'] as const
const QUEST_TYPES = ['quest', 'mini-quest', 'wish', 'want'] as const
const QUEST_STATUSES = ['active', 'completed', 'failed', 'dormant', 'potential'] as const
const CLOCK_SCOPES = ['world', 'room'] as const
const EFFECT_IMAGE_KINDS = ['item', 'npc', 'scene', 'companion'] as const
const ACTION_CATEGORIES = ['examine', 'take', 'use', 'attack', 'move', 'interact', 'ability', 'other'] as const
const ACTION_MODES = ['test', 'do'] as const
const ITEM_ACTION_AVAILABILITY = ['inventory', 'room', 'any'] as const
const ITEM_ACTION_TARGET_HINTS = ['none', 'self', 'active_npc'] as const

const AttrKeySchema = z.enum(ATTR_KEYS)
const QuestTypeSchema = z.enum(QUEST_TYPES)
const QuestStatusSchema = z.enum(QUEST_STATUSES)
const ActionCategorySchema: z.ZodType<ActionCategory> = z.enum(ACTION_CATEGORIES)
const ActionModeSchema: z.ZodType<ActionMode> = z.enum(ACTION_MODES)

const PrimitiveValueSchema = z.union([z.string(), z.number().finite(), z.boolean(), z.null()])

const SuggestionSchema = z.object({
  short: z.string().trim().min(1).max(20),
  full: z.string().trim().min(1).max(180),
})

const TraitSchema = z.object({
  name: z.string().trim().min(1).max(80),
  desc: z.string().trim().min(1).max(240),
})

const SkillSchema = z.object({
  id: z.string().trim().min(1).max(80),
  name: z.string().trim().min(1).max(80),
  level: z.number().finite().int().min(1).max(100),
  desc: z.string().trim().min(1).max(240).optional(),
})

const AbilityArgSchema = z.object({
  name: z.string().trim().min(1).max(80),
  type: z.enum(['target', 'number', 'choice', 'text']),
  prompt: z.string().trim().min(1).max(160),
  options: z.array(z.string().trim().min(1).max(80)).max(12).optional(),
  min: z.number().finite().optional(),
  max: z.number().finite().optional(),
  step: z.number().finite().optional(),
  defaultValue: z.union([z.string(), z.number().finite()]).optional(),
  ui: z.enum(['combo', 'slider', 'input']).optional(),
})

const AbilitySchema = z.object({
  id: z.string().trim().min(1).max(80),
  name: z.string().trim().min(1).max(80),
  desc: z.string().trim().min(1).max(400),
  cooldown: z.number().finite().int().min(0).max(1440),
  args: z.array(AbilityArgSchema).max(8).optional(),
})

const CompanionMotiveSchema = z.object({
  desc: z.string().trim().min(1).max(200),
  priority: z.enum(['high', 'medium', 'low']),
})

const CompanionFeelingSchema = z.object({
  target: z.string().trim().min(1).max(80),
  sentiment: z.enum(['positive', 'negative', 'neutral']),
  intensity: z.number().finite().int().min(-100).max(100),
})

const NPCFeelingSchema = z.object({
  sentiment: z.enum(['positive', 'negative', 'neutral']),
  intensity: z.number().finite().int().min(-100).max(100),
})

const NPCSchema = z.object({
  name: z.string().trim().min(1).max(80),
  identity: z.string().trim().min(1).max(120),
  lore: z.string().trim().min(1).max(600),
  sex: z.string().trim().min(1).max(40).optional(),
  stance: z.enum(['Hostile', 'Wary', 'Open']).optional(),
  intentions: z.array(z.string().trim().min(1).max(120)).max(6).optional(),
  feelings: z.record(z.string().trim().min(1).max(80), NPCFeelingSchema).optional(),
  relationship: z.number().finite().int().min(-100).max(100).optional(),
  memory: z.array(z.string().trim().min(1).max(180)).max(16).optional(),
  inventory: z.array(z.string().trim().min(1).max(80)).max(24).optional(),
  activeEffects: z.array(z.string().trim().min(1).max(42)).max(8).optional(),
})

const StatsSchema = z.object({
  STR: z.number().finite().int().min(1).max(100).optional(),
  DEX: z.number().finite().int().min(1).max(100).optional(),
  CON: z.number().finite().int().min(1).max(100).optional(),
  INT: z.number().finite().int().min(1).max(100).optional(),
  WIS: z.number().finite().int().min(1).max(100).optional(),
  CHA: z.number().finite().int().min(1).max(100).optional(),
  PER: z.number().finite().int().min(1).max(100).optional(),
  LCK: z.number().finite().int().min(1).max(100).optional(),
  WIL: z.number().finite().int().min(1).max(100).optional(),
  FOR: z.number().finite().int().min(1).max(100).optional(),
  AGI: z.number().finite().int().min(1).max(100).optional(),
  PRS: z.number().finite().int().min(1).max(100).optional(),
})

const RawGenesisCompanionSchema: z.ZodType<RawGenesisCompanion> = z.object({
  name: z.string().trim().min(1).max(80),
  identity: z.string().trim().min(1).max(120),
  bio: z.string().trim().min(1).max(600),
  sex: z.string().trim().min(1).max(40).optional(),
  vitals: z.object({
    vitality: z.number().finite().int().min(0).max(999),
    spirit: z.number().finite().int().min(0).max(999),
    essence: z.number().finite().int().min(0).max(999),
    maxVitality: z.number().finite().int().min(1).max(999).optional(),
    maxSpirit: z.number().finite().int().min(1).max(999).optional(),
    maxEssence: z.number().finite().int().min(1).max(999).optional(),
  }) as z.ZodType<RawGenesisCompanion['vitals']>,
  attributes: StatsSchema.transform(stats => normalizeStats(stats)) as z.ZodType<Record<AttributeKey, number>>,
  skills: z.array(SkillSchema).max(12).optional().default([]),
  abilities: z.array(AbilitySchema).max(12).optional().default([]),
  traits: z.array(TraitSchema).max(8).optional().default([]),
  inventory: z.array(z.string().trim().min(1).max(80)).max(24).optional().default([]),
  motives: z.array(CompanionMotiveSchema).max(8).optional().default([]),
  feelings: z.array(CompanionFeelingSchema).max(8).optional().default([]),
  companions: z.lazy(() => z.array(RawGenesisCompanionSchema).max(4)).optional(),
})

const GenesisQuestSchema = z.object({
  id: z.string().trim().min(1).max(80),
  title: z.string().trim().min(1).max(120),
  desc: z.string().trim().min(1).max(400),
  type: QuestTypeSchema,
  status: QuestStatusSchema.optional().default('active').refine(status => status !== 'completed' && status !== 'failed'),
})

const GenesisResponseSchema: z.ZodType<GenesisAIResponse> = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  identity: z.string().trim().min(1).max(120).optional(),
  sex: z.string().trim().min(1).max(40).optional(),
  stats: StatsSchema.transform(stats => normalizeStats(stats)) as z.ZodType<Record<AttributeKey, number>>,
  traits: z.array(TraitSchema).max(8).optional().default([]),
  skills: z.array(SkillSchema).max(12).optional().default([]),
  abilities: z.array(AbilitySchema).max(12).optional().default([]),
  inventory: z.array(z.string().trim().min(1).max(80)).max(24).optional().default([]),
  quests: z.array(GenesisQuestSchema).max(8).optional().default([]),
  companions: z.array(RawGenesisCompanionSchema).max(4).optional().default([]),
  room: z.object({
    name: z.string().trim().min(1).max(120),
    desc: z.string().trim().min(1).max(1200),
    narration: z.string().trim().min(1).max(1600),
    items: z.array(z.string().trim().min(1).max(80)).max(24).optional().default([]),
    npcs: z.array(NPCSchema).max(8).optional().default([]),
    suggestions: z.array(SuggestionSchema).min(1).max(6).optional().default([{ short: 'LOOK', full: 'Look around carefully.' }]),
  }),
})

const ModifierSchema = z.object({
  val: z.number().finite().int().min(-100).max(100),
  label: z.string().trim().min(1).max(60),
  source: z.string().trim().min(1).max(80),
  expiresAt: z.number().finite().int().optional(),
})

const ItemActionDescriptorSchema = z.object({
  id: z.string().trim().min(1).max(80),
  label: z.string().trim().min(1).max(80),
  inputTemplate: z.string().trim().min(1).max(180),
  mode: ActionModeSchema,
  category: ActionCategorySchema,
  when: z.enum(ITEM_ACTION_AVAILABILITY).optional(),
  targetHint: z.enum(ITEM_ACTION_TARGET_HINTS).optional(),
  execution: z.enum(['shared', 'item_forge']).optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(8).optional(),
})

const ItemActionHintsSchema = z.object({
  readable: z.boolean(),
  presentable: z.boolean(),
  selfUse: z.boolean(),
  targetUse: z.boolean(),
  lightSource: z.boolean(),
})

const ForgedItemPayloadSchema = z.union([
  z.string().trim().min(1).max(80),
  z.object({
    name: z.string().trim().min(1).max(80).optional(),
    item: z.string().trim().min(1).max(80).optional(),
    desc: z.string().trim().min(1).max(240).optional(),
    icon: z.string().trim().min(1).max(16).optional(),
    worth: z.number().finite().int().min(0).max(1_000_000).optional(),
    actions: z.array(ItemActionDescriptorSchema).max(8).optional(),
  }).transform((entry): ForgedItemPayload => ({
    name: String(entry.name ?? entry.item ?? '').trim(),
    ...(entry.desc ? { desc: entry.desc.trim() } : {}),
    ...(entry.icon ? { icon: entry.icon.trim() } : {}),
    ...(typeof entry.worth === 'number' ? { worth: entry.worth } : {}),
    ...(Array.isArray(entry.actions) ? { actions: entry.actions } : {}),
  })),
])

const ClockUpdateRecordSchema = z.record(
  z.string().trim().min(1).max(80),
  z.number().finite().int().min(-12).max(12),
)

const ScheduledEffectJobRequestSchema: z.ZodType<ScheduledEffectJobRequest> = z.object({
  kind: z.enum(['trigger_event', 'world_log']),
  delayMinutes: z.number().finite().int().min(0).max(1440),
  note: z.string().trim().min(1).max(180).optional(),
  event: z.unknown().transform((value, ctx): GameEventState => {
    const parsed = parseGameEventState(value)
    if (!parsed) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid scheduled event artifact' })
      return z.NEVER
    }
    return parsed
  }).optional(),
}).superRefine((value, ctx) => {
  if (value.kind === 'trigger_event' && !value.event) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'trigger_event schedule jobs require an event payload',
      path: ['event'],
    })
  }
})

const LogicArtifactReplacementsSchema: z.ZodType<LogicArtifactReplacements> = z.object({
  activeEvent: z.union([
    z.null(),
    z.unknown().transform((value, ctx): LogicArtifactReplacements['activeEvent'] => {
      const parsed = parseGameEventState(value)
      if (!parsed) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid activeEvent artifact replacement' })
        return z.NEVER
      }
      return parsed
    }),
  ]).optional(),
})

const LogicUpdateSchema: z.ZodType<LogicUpdate> = z.object({
  successLevel: z.number().finite().int().min(0).max(2),
  updates: z.object({
    vitality: z.number().finite().int().min(-200).max(200).optional(),
    spirit: z.number().finite().int().min(-200).max(200).optional(),
    essence: z.number().finite().int().min(-200).max(200).optional(),
    timeCost: z.number().finite().int().min(1).max(1440),
    mods: z.record(z.string().trim().min(1).max(6), ModifierSchema).optional(),
    inventoryAdd: z.array(ForgedItemPayloadSchema).max(24).optional(),
    inventoryRemove: z.array(z.string().trim().min(1).max(80)).max(24).optional(),
    inventoryEquip: z.array(z.string().trim().min(1).max(80)).max(24).optional(),
    inventoryUnequip: z.array(z.string().trim().min(1).max(80)).max(24).optional(),
    roomItemsAdd: z.array(ForgedItemPayloadSchema).max(24).optional(),
    roomItemsRemove: z.array(z.string().trim().min(1).max(80)).max(24).optional(),
    roomNpcsAdd: z.array(z.object({
      name: z.string().trim().min(1).max(80),
      identity: z.string().trim().min(1).max(120),
      lore: z.string().trim().min(1).max(400).optional(),
      sex: z.string().trim().min(1).max(40).optional(),
    })).max(6).optional(),
    roomFlags: z.record(z.string().trim().min(1).max(80), PrimitiveValueSchema).optional(),
    clockUpdates: ClockUpdateRecordSchema.optional(),
    questUpdates: z.array(z.object({
      id: z.string().trim().min(1).max(80),
      newStatus: QuestStatusSchema,
    })).max(8).optional(),
    npcRelationshipUpdates: z.record(z.string().trim().min(1).max(80), z.number().finite().int().min(-100).max(100)).optional(),
    npcMemoryAdd: z.record(z.string().trim().min(1).max(80), z.string().trim().min(1).max(180)).optional(),
    scheduleJobs: z.array(ScheduledEffectJobRequestSchema).max(6).optional(),
    artifactReplacements: LogicArtifactReplacementsSchema.optional(),
    triggerEvent: z.unknown().transform((value, ctx): GameEventState => {
      const parsed = parseGameEventState(value)
      if (!parsed) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid event artifact' })
        return z.NEVER
      }
      return parsed
    }).optional(),
    clearEvent: z.boolean().optional(),
  }).transform((updates) => {
    if (updates.artifactReplacements) return updates
    if (updates.clearEvent) {
      return {
        ...updates,
        artifactReplacements: { activeEvent: null },
      }
    }
    if (updates.triggerEvent) {
      return {
        ...updates,
        artifactReplacements: { activeEvent: updates.triggerEvent },
      }
    }
    return updates
  }),
})

const NpcModEffectSchema = z.object({
  val: z.number().finite().int().min(-100).max(100),
  label: z.string().trim().min(1).max(60),
  source: z.string().trim().min(1).max(80),
  duration: z.number().finite().int().min(1).max(1440).optional(),
})

const NpcLogicUpdateSchema: z.ZodType<NpcLogicUpdate> = z.object({
  hit: z.boolean(),
  vitalityDelta: z.number().finite().int().min(-200).max(200),
  spiritDelta: z.number().finite().int().min(-200).max(200).optional(),
  essenceDelta: z.number().finite().int().min(-200).max(200).optional(),
  mods: z.record(z.string().trim().min(1).max(6), NpcModEffectSchema).optional(),
  inventorySteal: z.string().trim().min(1).max(80).optional(),
  outcome: z.string().trim().min(1).max(400),
})

const WorldTickNpcUpdateSchema = z.object({
  id: z.string().trim().min(1).max(80),
  moveTo: z.string().trim().regex(/^-?\d+_-?\d+_-?\d+$/).optional(),
  intentions: z.array(z.string().trim().min(1).max(120)).max(4).optional(),
  relationship: z.number().finite().int().min(-100).max(100).optional(),
  memoryAdd: z.string().trim().min(1).max(180).optional(),
  activeEffects: z.array(z.string().trim().min(1).max(42)).max(6).optional(),
  dormantEffectsAdd: z.object({
    label: z.string().trim().min(1).max(42),
    source: z.string().trim().min(1).max(80).optional(),
    attr: AttrKeySchema.optional(),
  }).optional(),
})

const WorldTickUpdateSchema: z.ZodType<WorldTickUpdate> = z.object({
  clockUpdates: ClockUpdateRecordSchema.optional(),
  npcUpdates: z.array(WorldTickNpcUpdateSchema).max(8).optional(),
  newNpcs: z.array(z.object({
    location: z.string().trim().regex(/^-?\d+_-?\d+_-?\d+$/),
    name: z.string().trim().min(1).max(80),
    identity: z.string().trim().min(1).max(120),
    lore: z.string().trim().min(1).max(600),
    sex: z.string().trim().min(1).max(40).optional(),
    intentions: z.array(z.string().trim().min(1).max(120)).max(6),
  })).max(2).optional(),
  newItems: z.array(z.object({
    location: z.string().trim().regex(/^-?\d+_-?\d+_-?\d+$/),
    name: z.string().trim().min(1).max(80),
  })).max(4).optional(),
  questUpdates: z.array(z.object({
    id: z.string().trim().min(1).max(80),
    newStatus: z.enum(['active', 'failed', 'dormant', 'potential'] satisfies readonly QuestStatus[]),
    note: z.string().trim().min(1).max(200).optional(),
  })).max(8).optional(),
  newNodes: z.array(z.object({
    key: z.string().trim().regex(/^-?\d+_-?\d+_-?\d+$/),
    name: z.string().trim().min(1).max(120),
    desc: z.string().trim().min(1).max(600),
  })).max(4).optional(),
  worldEvents: z.array(z.string().trim().min(1).max(180)).max(8).optional(),
  roomFlagUpdates: z.array(z.object({
    roomKey: z.string().trim().regex(/^-?\d+_-?\d+_-?\d+$/),
    flags: z.record(z.string().trim().min(1).max(80), PrimitiveValueSchema),
  })).max(8).optional(),
})

const TradeSeedSchema = z.object({
  offer: z.string().trim().min(1).max(80),
  worth: z.number().finite().int().min(0).max(1_000_000).optional(),
})

const WorldForgeNpcSchema = z.object({
  name: z.string().trim().min(1).max(80),
  identity: z.string().trim().min(1).max(120),
  lore: z.string().trim().min(1).max(600),
  sex: z.string().trim().min(1).max(40),
  intentions: z.array(z.string().trim().min(1).max(120)).max(6),
  relationship: z.number().finite().int().min(-100).max(100),
  memory: z.array(z.string().trim().min(1).max(180)).max(10),
  inventory: z.array(z.string().trim().min(1).max(80)).max(24),
})

const WorldForgeNodeSchema = z.object({
  key: z.string().trim().regex(/^-?\d+_-?\d+_-?\d+$/),
  name: z.string().trim().min(1).max(120),
  desc: z.string().trim().min(1).max(800),
  items: z.array(z.string().trim().min(1).max(80)).max(24),
  npcs: z.array(WorldForgeNpcSchema).max(8),
  spawns: z.array(z.object({
    archetype: z.string().trim().min(1).max(80),
    level: z.number().finite().int().min(-10).max(20),
    chancePerHour: z.number().finite().min(0).max(1),
    lootInfo: z.string().trim().min(1).max(120),
    friendliness: z.number().finite().int().min(-10).max(10),
    alertness: z.string().trim().min(1).max(80),
  })).max(4).optional().default([]),
})

const WorldForgePayloadSchema = z.object({
  nodes: z.array(WorldForgeNodeSchema).max(10),
})

const MusicPayloadSchema = z.union([
  z.array(z.record(z.string(), z.unknown())).max(80),
  z.object({
    notes: z.array(z.record(z.string(), z.unknown())).max(80).optional(),
    score: z.array(z.record(z.string(), z.unknown())).max(80).optional(),
    phrase: z.array(z.record(z.string(), z.unknown())).max(80).optional(),
    data: z.array(z.record(z.string(), z.unknown())).max(80).optional(),
  }).passthrough(),
])

function normalizeStats(stats: Partial<Record<AttributeKey, number>>): Record<AttributeKey, number> {
  return {
    STR: clampStat(stats.STR),
    DEX: clampStat(stats.DEX),
    CON: clampStat(stats.CON),
    INT: clampStat(stats.INT),
    WIS: clampStat(stats.WIS),
    CHA: clampStat(stats.CHA),
    PER: clampStat(stats.PER),
    LCK: clampStat(stats.LCK),
    WIL: clampStat(stats.WIL),
    FOR: clampStat(stats.FOR),
    AGI: clampStat(stats.AGI),
    PRS: clampStat(stats.PRS),
  }
}

function clampStat(value: unknown): number {
  const num = Number(value)
  if (!Number.isFinite(num)) return 10
  return Math.max(1, Math.min(100, Math.round(num)))
}

export function parseLogicUpdate(input: unknown): LogicUpdate | null {
  return LogicUpdateSchema.safeParse(input).success ? LogicUpdateSchema.parse(input) : null
}

export function parseNpcLogicUpdate(input: unknown): NpcLogicUpdate | null {
  return NpcLogicUpdateSchema.safeParse(input).success ? NpcLogicUpdateSchema.parse(input) : null
}

export function parseGenesisAIResponse(input: unknown): GenesisAIResponse | null {
  const parsed = GenesisResponseSchema.safeParse(input)
  return parsed.success ? parsed.data : null
}

export function parseTradeSeed(input: unknown): { offer: string; worth?: number } | null {
  const parsed = TradeSeedSchema.safeParse(input)
  return parsed.success ? parsed.data : null
}

export function parseItemActionHints(input: unknown): {
  readable: boolean
  presentable: boolean
  selfUse: boolean
  targetUse: boolean
  lightSource: boolean
} | null {
  const parsed = ItemActionHintsSchema.safeParse(input)
  return parsed.success ? parsed.data : null
}

export function parseWorldTickUpdate(input: unknown): WorldTickUpdate | null {
  const parsed = WorldTickUpdateSchema.safeParse(input)
  return parsed.success ? parsed.data : null
}

export function parseWorldForgeNodes(input: unknown): Array<{
  key: string
  name: string
  desc: string
  items: string[]
  npcs: Array<{
    name: string
    identity: string
    lore: string
    sex: string
    intentions: string[]
    relationship: number
    memory: string[]
    inventory: string[]
  }>
  spawns?: Array<{
    archetype: string
    level: number
    chancePerHour: number
    lootInfo: string
    friendliness: number
    alertness: string
  }>
}> | null {
  const parsed = WorldForgePayloadSchema.safeParse(input)
  return parsed.success ? parsed.data.nodes : null
}

export function extractMusicForgeNotes(input: unknown): Array<Record<string, unknown>> | null {
  const parsed = MusicPayloadSchema.safeParse(input)
  if (!parsed.success) return null
  if (Array.isArray(parsed.data)) return parsed.data
  return parsed.data.notes ?? parsed.data.score ?? parsed.data.phrase ?? parsed.data.data ?? null
}

export function normalizeProgressClockSeed(input: unknown): ProgressClock | null {
  const schema = z.object({
    id: z.string().trim().min(1).max(80),
    label: z.string().trim().min(1).max(80),
    segments: z.number().finite().int().min(2).max(12),
    filled: z.number().finite().int().min(0).max(12),
    trigger: z.string().trim().min(1).max(140).optional(),
  }).refine(clock => clock.filled <= clock.segments, 'filled must not exceed segments')
  const parsed = schema.safeParse(input)
  return parsed.success ? parsed.data : null
}
